import toast from 'react-hot-toast';
import {
  Info,
  XIcon,
  CircleAlertIcon,
  CircleXIcon,
  CircleCheckIcon,
  Loader2,
  CheckCheck,
  AlertTriangle,
  Bell,
  type LucideIcon,
} from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@src/design-system/helpers';

import type { Toast } from 'react-hot-toast';

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
}

// Toast Container Variants
const toastVariants = cva('flex gap-2 rounded-lg bg-white shadow-lg relative', {
  variants: {
    variant: {
      success: '',
      error: '',
      warning: '',
      info: '',
      loading: '',
      promise: '',
      default: '',
      destructive: '',
    },
    size: {
      sm: 'w-[280px] p-2',
      md: 'w-[340px] p-3',
      lg: 'w-[400px] p-4',
    },
    layout: {
      compact: 'items-center',
      default: '',
      expanded: 'flex-col',
    },
  },
  defaultVariants: {
    variant: 'info',
    size: 'md',
    layout: 'default',
  },
});

// Icon Container Variants
const iconVariants = cva(
  'aspect-square flex justify-center items-center rounded-lg flex-shrink-0',
  {
    variants: {
      variant: {
        success: 'bg-green-500/10 text-green-500',
        error: 'bg-red-500/10 text-red-500',
        warning: 'bg-yellow-500/10 text-yellow-500',
        info: 'bg-blue-500/10 text-blue-500',
        loading: 'bg-purple-500/10 text-purple-500',
        promise: 'bg-cyan-500/10 text-cyan-500',
        default: 'bg-gray-500/10 text-gray-500',
        destructive: 'bg-destructive/10 text-destructive',
      },
      size: {
        sm: 'size-7',
        md: 'size-8',
        lg: 'size-10',
      },
      layout: {
        compact: 'self-center',
        default: 'self-start',
        expanded: 'self-start',
      },
    },
    defaultVariants: {
      variant: 'info',
      size: 'md',
      layout: 'default',
    },
  }
);

// Icon Size Variants
const iconSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// Title Variants
const titleVariants = cva('font-medium text-gray-900 line-clamp-2 block', {
  variants: {
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// Description Variants
const descriptionVariants = cva('text-gray-700 mt-0.5', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    },
    layout: {
      compact: 'line-clamp-1',
      default: 'line-clamp-3',
      expanded: 'line-clamp-3',
    },
  },
  defaultVariants: {
    size: 'md',
    layout: 'default',
  },
});

// Close Button Variants
const closeButtonVariants = cva('text-gray-500', {
  variants: {
    size: {
      sm: 'size-3',
      md: 'size-4',
      lg: 'size-5',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// Action Button Variants
const actionButtonVariants = cva(
  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
  {
    variants: {
      variant: {
        primary: 'bg-primary-600 text-white hover:bg-primary-700',
        secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        ghost: 'text-gray-700 hover:bg-gray-100',
        outline:
          'h-10 rounded-xl border border-primary-200 px-5 text-gray-800 hover:bg-primary-30',
      },
    },
    defaultVariants: {
      variant: 'ghost',
    },
  }
);

// Icon mapping for variants
const variantIconMap = {
  success: CircleCheckIcon,
  error: CircleXIcon,
  warning: CircleAlertIcon,
  info: Info,
  loading: Loader2,
  promise: CheckCheck,
  default: Bell,
  destructive: AlertTriangle,
} as const;

export interface CustomToastProps extends VariantProps<typeof toastVariants> {
  t?: Toast;
  title: string;
  description?: string;
  dismissible?: boolean;
  showIcon?: boolean;
  customIcon?: LucideIcon | React.ReactNode;
  renderExtraContent?: React.ReactNode;
  actions?: ToastAction[];
  className?: string;
  duration?: number;
  position?: Toast['position'];
}

const renderIcon = (
  variant: NonNullable<CustomToastProps['variant']>,
  customIcon?: LucideIcon | React.ReactNode,
  size?: NonNullable<CustomToastProps['size']>
) => {
  if (customIcon) {
    if (typeof customIcon === 'function') {
      const CustomIconComponent = customIcon as LucideIcon;
      return <CustomIconComponent className={iconSizeVariants({ size })} />;
    }
    return customIcon;
  }

  const IconComponent = variantIconMap[variant];
  const isLoading = variant === 'loading';

  return (
    <IconComponent
      className={cn(iconSizeVariants({ size }), isLoading && 'animate-spin')}
    />
  );
};

export const CustomToast = ({
  t,
  dismissible = true,
  description,
  title,
  variant = 'info',
  size = 'md',
  layout = 'default',
  showIcon = true,
  customIcon,
  renderExtraContent,
  actions,
  className,
}: CustomToastProps) => {
  const toastVariant = variant || 'info';
  const toastSize = size || 'md';
  const toastLayout = layout || 'default';

  return (
    <div
      className={cn(
        toastVariants({
          variant: toastVariant,
          size: toastSize,
          layout: toastLayout,
          className,
        })
      )}
    >
      {/* Icon Section */}
      {showIcon && (
        <span
          className={iconVariants({
            variant: toastVariant,
            size: toastSize,
            layout: toastLayout,
          })}
        >
          {renderIcon(toastVariant, customIcon, toastSize)}
        </span>
      )}

      {/* Content Section */}
      <div
        className={cn('flex-1 min-w-0', toastLayout === 'expanded' && 'w-full')}
      >
        <span className={titleVariants({ size: toastSize })}>{title}</span>

        {description && (
          <p
            className={descriptionVariants({
              size: toastSize,
              layout: toastLayout,
            })}
          >
            {description}
          </p>
        )}

        {/* Extra Content */}
        {renderExtraContent && <div className='mt-2'>{renderExtraContent}</div>}

        {/* Action Buttons */}
        {actions && actions.length > 0 && (
          <div className='flex gap-2 mt-3'>
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={cn(
                  actionButtonVariants({ variant: action.variant })
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Close Button */}
      {t?.id && dismissible && (
        <button
          onClick={() => toast.dismiss(t.id)}
          className={cn(
            'absolute right-2 top-3 cursor-pointer hover:bg-gray-100 rounded-md p-1.5 transition-colors',
            toastLayout === 'compact' && 'relative right-0 top-0'
          )}
          aria-label='Dismiss'
        >
          <XIcon className={closeButtonVariants({ size: toastSize })} />
        </button>
      )}
    </div>
  );
};

// Export variants for reusability
/* eslint-disable react-refresh/only-export-components */
export {
  toastVariants,
  iconVariants,
  iconSizeVariants,
  titleVariants,
  descriptionVariants,
  closeButtonVariants,
  actionButtonVariants,
};
/* eslint-enable react-refresh/only-export-components */
